/**
 * Financial Summary Card Component
 * Displays financial KPIs and summary statistics
 */

import React from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  alpha,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import type { FinancialSummary } from '../../../../types/finance';

interface FinancialSummaryCardProps {
  summary: FinancialSummary;
}

const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
};

export const FinancialSummaryCard: React.FC<FinancialSummaryCardProps> = ({ summary }) => {
  const theme = useTheme();

  const KPICard = ({ 
    label, 
    value, 
    color = theme.palette.primary.main 
  }: { 
    label: string; 
    value: number; 
    color?: string;
  }) => (
    <Card>
      <CardContent>
        <Typography
          variant="caption"
          sx={{
            color: theme.palette.text.secondary,
            fontSize: theme.typography.caption.fontSize,
            marginBottom: theme.spacing(0.5),
            display: 'block',
          }}
        >
          {label}
        </Typography>
        <Typography
          variant="h5"
          sx={{
            fontWeight: 700,
            color: color,
            fontSize: theme.typography.h4.fontSize,
          }}
        >
          {formatCurrency(value)}
        </Typography>
      </CardContent>
    </Card>
  );

  return (
    <Box sx={{ marginBottom: theme.spacing(3) }}>
      <Typography
        variant="h6"
        sx={{
          fontWeight: theme.typography.h6.fontWeight,
          marginBottom: theme.spacing(2),
          color: theme.palette.text.primary,
          fontSize: theme.typography.body1.fontSize,
        }}
      >
        Financial Overview
      </Typography>
      
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={3}>
          <KPICard 
            label="Total Revenue" 
            value={summary.totalRevenue}
            color={theme.palette.success.main}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KPICard 
            label="Booking Fees" 
            value={summary.totalBookingFees}
            color={theme.palette.info.main}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KPICard 
            label="Platform Fees" 
            value={summary.totalPlatformFees}
            color={theme.palette.info.light}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KPICard 
            label="Ghost Payouts" 
            value={summary.totalGhostPayouts}
            color={theme.palette.warning.main}
          />
        </Grid>
        {/* <Grid item xs={12}>
          <Card sx={{ backgroundColor: theme.palette.primary.main, color: theme.palette.primary.contrastText }}>
            <CardContent>
              <Typography
                variant="caption"
                sx={{
                  color: alpha(theme.palette.common.white, 0.7),
                  fontSize: theme.typography.caption.fontSize,
                  marginBottom: theme.spacing(0.5),
                  display: 'block',
                }}
              >
                Net Income
              </Typography>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 700,
                  color: theme.palette.common.white,
                  fontSize: theme.typography.h2.fontSize,
                }}
              >
                {formatCurrency(summary.netIncome)}
              </Typography>
            </CardContent>
          </Card>
        </Grid> */}
      </Grid>
    </Box>
  );
};
